import keyBy from 'lodash/keyBy';
import type {
	ApiDependencyIssueLinkType,
	ApiIssueLinkType,
	QueryEndpointResponse,
} from '@atlassian/jira-router-resources-advanced-roadmaps-settings/src/services/issue-link/types';
import type { Dependency, IssueLinkType } from '../common/types';
import type { OriginalState } from './types';

export const toDraft = (
	dependencyIds: Dependency['issueLinkTypeId'][],
	dependencies: Record<string, Dependency>,
): Dependency[] =>
	dependencyIds.map((id) => dependencies[id]).filter((dependency) => dependency !== undefined);

export const fromDraft = (draft: Dependency[]) => ({
	dependencyIds: draft.map((dependency) => dependency.issueLinkTypeId),
	dependencies: draft.reduce<Record<string, Dependency>>((acc, dependency) => {
		acc[dependency.issueLinkTypeId] = dependency;
		return acc;
	}, {}),
});

/** Return the body for the update post request. */
export const getUpdateBody = (draft: Dependency[]) => ({
	dependencyIssueLinkTypes: draft.map(({ issueLinkTypeId, direction }) => ({
		issueLinkTypeId,
		isOutward: direction === 'OUTWARD',
	})),
});

const transformIssueLinkType = ({
	id,
	name,
	inward,
	outward,
}: ApiIssueLinkType): IssueLinkType => ({
	id,
	name,
	inwardDescription: inward,
	outwardDescription: outward,
});

const transformDependency = ({
	issueLinkTypeId,
	isOutward,
}: ApiDependencyIssueLinkType): Dependency => ({
	issueLinkTypeId,
	direction: isOutward ? 'OUTWARD' : 'INWARD',
});

const transformIssueLinkTypes = (
	issueLinkTypes: ApiIssueLinkType[],
): Record<string, IssueLinkType> => {
	const transformedIssueLinkTypes = issueLinkTypes.map(transformIssueLinkType);
	return keyBy(transformedIssueLinkTypes, (it: IssueLinkType) => it.id);
};

const transformDependencies = (
	dependencyIssueLinkTypes: ApiDependencyIssueLinkType[],
): Record<string, Dependency> => {
	const transformedDependencies = dependencyIssueLinkTypes.map(transformDependency);
	return keyBy(transformedDependencies, (it: Dependency) => it.issueLinkTypeId);
};

export const mapToOriginalState = ({
	dependencySettings,
	issueLinks,
}: QueryEndpointResponse): OriginalState => ({
	isIssueLinkingEnabled: issueLinks.isIssueLinkingEnabled,
	issueLinkTypes: transformIssueLinkTypes(issueLinks.issueLinkTypes),
	issueLinkTypeIds: issueLinks.issueLinkTypes.map((it) => it.id),
	dependencies: transformDependencies(dependencySettings.dependencyIssueLinkTypes),
	dependencyIds: dependencySettings.dependencyIssueLinkTypes.map((it) => it.issueLinkTypeId),
});
